import { useEffect, useRef } from 'react';

/**
 * Poll with failure backoff and jitter.
 *
 * Fixed-interval polling amplifies outages: when the origin slows down,
 * every viewer in a room keeps firing at full rate in lockstep, which is
 * exactly what turned transient slowness into 504/525 bursts during a
 * multi-person livestream. Instead:
 *
 * - on success, run again after baseMs (±10% jitter so a room's viewers
 *   drift apart instead of stampeding together);
 * - on failure, double the delay per consecutive failure up to maxMs, then
 *   reset to baseMs on the first success.
 *
 * `poll` must be referentially stable (useCallback) — a new identity
 * restarts the loop. onError/onRecover are held in refs so their identity
 * doesn't.
 */
export function usePollWithBackoff(
  poll: () => Promise<unknown>,
  baseMs: number,
  {
    maxMs = 30_000,
    immediate = false,
    onError,
    onRecover,
  }: {
    maxMs?: number;
    /** Fire the first poll right away instead of after the first interval. */
    immediate?: boolean;
    onError?: (err: unknown, consecutiveFailures: number) => void;
    onRecover?: (failuresBeforeRecovery: number) => void;
  } = {},
): void {
  const onErrorRef = useRef(onError);
  const onRecoverRef = useRef(onRecover);
  onErrorRef.current = onError;
  onRecoverRef.current = onRecover;

  useEffect(() => {
    let cancelled = false;
    let failures = 0;
    let first = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      if (cancelled) return;
      if (first && immediate) {
        first = false;
        timer = setTimeout(run, 0);
        return;
      }
      first = false;
      const delay = Math.min(baseMs * 2 ** failures, maxMs);
      const jittered = delay * (0.9 + Math.random() * 0.2);
      timer = setTimeout(run, jittered);
    };

    const run = () => {
      poll()
        .then(() => {
          if (failures > 0) onRecoverRef.current?.(failures);
          failures = 0;
        })
        .catch((err: unknown) => {
          failures += 1;
          onErrorRef.current?.(err, failures);
        })
        .finally(schedule);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [poll, baseMs, maxMs, immediate]);
}

/** Best-effort status label for an API error (axios or otherwise). */
export function pollErrorStatus(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number } }).response;
    if (response?.status) return String(response.status);
  }
  return 'network';
}
